import posthog from "posthog-js";

import { onTenantChange, useTenantStore } from "./tenant-store";

let currentStorageFirebaseUid: string | null = null;
const userChangeListeners = new Set<(firebaseUid: string | null) => void>();
const suppressedUserTenantWrites = new Set<string>();

type UserTenantStorageIssue =
  | "load_failed"
  | "missing_firebase_uid_scope"
  | "missing_tenant_scope"
  | "persist_failed";

const activeUserTenantStorageIssues = new Set<string>();

const captureUserTenantStorageIssue = (
  storeName: string,
  issue: UserTenantStorageIssue,
  error?: unknown,
) => {
  const issueKey = `${storeName}:${issue}`;
  if (activeUserTenantStorageIssues.has(issueKey)) return;
  activeUserTenantStorageIssues.add(issueKey);

  try {
    posthog.capture("user_tenant_storage_issue", {
      store_name: storeName,
      issue,
      has_tenant_scope: Boolean(useTenantStore.getState().getCurrentTenant()),
      has_firebase_uid_scope: Boolean(currentStorageFirebaseUid),
      ...(error instanceof Error ? { error_name: error.name } : {}),
    });
  } catch {
    // Telemetry must never break preference storage.
  }
};

const clearUserTenantStorageIssue = (
  storeName: string,
  issue: UserTenantStorageIssue,
) => {
  activeUserTenantStorageIssues.delete(`${storeName}:${issue}`);
};

// Helper to get tenant-specific storage key
export const getTenantKey = (baseName: string, tenant: string | null) => {
  return tenant ? `${baseName}-${tenant}` : baseName;
};

// User-scoped stores need both parts of the key before they can read or persist
// a user's changes.
export const getUserTenantKey = (
  baseName: string,
  tenant: string | null,
  firebaseUid: string | null,
) => {
  return tenant && firebaseUid ? `${baseName}-${tenant}-${firebaseUid}` : null;
};

// True once both halves of the user scope are known, which is the point at
// which user-scoped stores hold that user's saved values rather than defaults.
// Consumers that drive a fetch from a persisted preference need this: the uid
// is committed after React has already rendered with `currentUser` set, so
// "the user is loaded" does not imply "their preferences are loaded".
export const hasUserTenantScope = () =>
  Boolean(useTenantStore.getState().getCurrentTenant()) &&
  Boolean(currentStorageFirebaseUid);

export const setCurrentStorageFirebaseUid = (firebaseUid: string | null) => {
  if (firebaseUid === currentStorageFirebaseUid) return;

  activeUserTenantStorageIssues.clear();
  currentStorageFirebaseUid = firebaseUid;
  userChangeListeners.forEach((listener) => listener(firebaseUid));
};

const onStorageUserChange = (
  callback: (firebaseUid: string | null) => void,
) => {
  userChangeListeners.add(callback);
  return () => {
    userChangeListeners.delete(callback);
  };
};

// Helper to load tenant-specific data
export const loadTenantData = (storeName: string, tenant: string | null) => {
  try {
    const key = getTenantKey(storeName, tenant);
    const str = localStorage.getItem(key);
    if (str) {
      const data = JSON.parse(str);
      return data.state || data || {};
    }
  } catch (error) {
    console.warn(`Failed to load tenant ${storeName} data:`, error);
  }
  return {};
};

// Load data only when both the tenant and Firebase user scopes are known.
export const loadUserTenantData = (
  storeName: string,
  tenant: string | null,
  firebaseUid: string | null,
) => {
  try {
    const key = getUserTenantKey(storeName, tenant, firebaseUid);
    const str = key ? localStorage.getItem(key) : null;
    if (str) {
      const data = JSON.parse(str);
      if (data && typeof data === "object") {
        return data.state || data;
      }
    }
  } catch (error) {
    console.warn(`Failed to load user ${storeName} data:`, error);
    return {};
  }

  return {};
};

// Create tenant-aware storage for Zustand persist
export const createTenantStorage = () => ({
  getItem: (name: string) => {
    try {
      const currentTenant = useTenantStore.getState().getCurrentTenant();
      const key = getTenantKey(name, currentTenant);
      const str = localStorage.getItem(key);
      return str ? JSON.parse(str) : null;
    } catch {
      return null;
    }
  },
  setItem: (name: string, value: any) => {
    try {
      const currentTenant = useTenantStore.getState().getCurrentTenant();
      const key = getTenantKey(name, currentTenant);
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Safari Private Browsing blocks storage - fail silently
    }
  },
  removeItem: (name: string) => {
    try {
      const currentTenant = useTenantStore.getState().getCurrentTenant();
      const key = getTenantKey(name, currentTenant);
      localStorage.removeItem(key);
    } catch {
      // Safari Private Browsing blocks storage - fail silently
    }
  },
});

// Create user- and tenant-aware storage for authenticated user preferences.
export const createUserTenantStorage = () => ({
  getItem: (name: string) => {
    try {
      const currentTenant = useTenantStore.getState().getCurrentTenant();
      const key = getUserTenantKey(
        name,
        currentTenant,
        currentStorageFirebaseUid,
      );
      if (!key) return null;

      const str = localStorage.getItem(key);
      const value = str ? JSON.parse(str) : null;
      clearUserTenantStorageIssue(name, "load_failed");
      return value;
    } catch (error) {
      console.warn(`Failed to load user ${name} data:`, error);
      captureUserTenantStorageIssue(name, "load_failed", error);
      return null;
    }
  },
  setItem: (name: string, value: any) => {
    try {
      if (suppressedUserTenantWrites.has(name)) return;

      const currentTenant = useTenantStore.getState().getCurrentTenant();
      if (!currentTenant) {
        console.warn(`Skipped user ${name} write: missing tenant scope`);
        captureUserTenantStorageIssue(name, "missing_tenant_scope");
        return;
      }
      if (!currentStorageFirebaseUid) {
        console.warn(`Skipped user ${name} write: missing Firebase UID scope`);
        captureUserTenantStorageIssue(name, "missing_firebase_uid_scope");
        return;
      }

      const key = getUserTenantKey(
        name,
        currentTenant,
        currentStorageFirebaseUid,
      );
      if (!key) return;

      localStorage.setItem(key, JSON.stringify(value));
      clearUserTenantStorageIssue(name, "missing_tenant_scope");
      clearUserTenantStorageIssue(name, "missing_firebase_uid_scope");
      clearUserTenantStorageIssue(name, "persist_failed");
    } catch (error) {
      console.warn(`Failed to persist user ${name} data:`, error);
      captureUserTenantStorageIssue(name, "persist_failed", error);
    }
  },
  removeItem: (name: string) => {
    try {
      const currentTenant = useTenantStore.getState().getCurrentTenant();
      const key = getUserTenantKey(
        name,
        currentTenant,
        currentStorageFirebaseUid,
      );
      if (!key) return;

      localStorage.removeItem(key);
    } catch {
      // Safari Private Browsing blocks storage - fail silently
    }
  },
});

// Create tenant change listener for a store
export const createTenantChangeListener = (
  storeName: string,
  setState: (state: any) => void,
) => {
  return onTenantChange((tenant) => {
    // Load tenant-specific data (PER_TENANT is default)
    const tenantData = loadTenantData(storeName, tenant);
    setState(tenantData);
  });
};

// Reload user preferences whenever either part of the storage scope changes.
export const createUserTenantChangeListener = (
  storeName: string,
  setState: (state: any) => void,
) => {
  const loadCurrentScope = () => {
    const tenant = useTenantStore.getState().getCurrentTenant();
    const userData = loadUserTenantData(
      storeName,
      tenant,
      currentStorageFirebaseUid,
    );

    // Reloading the active scope updates the in-memory Zustand store. Suppress
    // the persist middleware's resulting redundant write.
    suppressedUserTenantWrites.add(storeName);
    try {
      setState(userData);
    } finally {
      suppressedUserTenantWrites.delete(storeName);
    }
  };

  const unsubscribeTenant = onTenantChange(loadCurrentScope);
  const unsubscribeUser = onStorageUserChange(loadCurrentScope);

  return () => {
    unsubscribeTenant();
    unsubscribeUser();
  };
};
